import React, { useState } from 'react';
import { ArrowRight, Bell, Search, Shield } from 'lucide-react';
import api from '../lib/adminApi';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Person, Page, BillRow, useReport, dateTime, humanize, endpoint, message } from './api';
import { BillRows, Empty, LoadState, Notice, Pagination, Status } from './shared';
export default function Users({
  refresh,
  retry,
  onUser,
}: {
  refresh: number;
  retry: () => void;
  onUser: (id: string) => void;
}) {
  const [q, setQ] = useState(''),
    [search, setSearch] = useState(''),
    [kind, setKind] = useState('registered'),
    [status, setStatus] = useState('all'),
    [offset, setOffset] = useState(0);
  const { data, loading, error } = useReport<Page<Person>>(
    '/users',
    {
      q: search,
      kind,
      status,
      offset,
    },
    refresh
  );
  return (
    <section className="admin-panel">
      <div className="admin-section-heading">
        <div>
          <h2>Find a person</h2>
          <p>Search by name, username, email, phone, or user ID.</p>
        </div>
      </div>
      <div className="admin-filters">
        <form
          className="admin-search"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(q);
            setOffset(0);
          }}
        >
          <Search size={18} />
          <input
            aria-label="Search users"
            placeholder="Search people…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button variant="ghost" type="submit">
            Search
          </Button>
        </form>
        <select
          aria-label="Profile type"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value);
            setOffset(0);
          }}
        >
          <option value="registered">Registered</option>
          <option value="all">All profiles</option>
        </select>
        <select
          aria-label="Account status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setOffset(0);
          }}
        >
          <option value="all">All statuses</option>
          {[
            'active',
            'pending_setup',
            'pending_phone_verify',
            'suspended',
            'blocked',
            'deleted',
          ].map((s) => (
            <option key={s} value={s}>
              {humanize(s)}
            </option>
          ))}
        </select>
      </div>
      <LoadState loading={loading} error={error} retry={retry} />
      {data && (
        <>
          {data.items.length ? (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Status</th>
                    <th className="numeric">Bills created</th>
                    <th>Latest bill activity</th>
                    <th>
                      <span className="sr-only">Investigate</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((person) => (
                    <tr key={person.id}>
                      <td>
                        <div className="admin-person-cell">
                          <span className="admin-avatar">
                            {person.name
                              .split(' ')
                              .map((n) => n[0])
                              .slice(0, 2)
                              .join('')}
                          </span>
                          <div>
                            <button className="admin-text-link" onClick={() => onUser(person.id)}>
                              {person.name}
                            </button>
                            <small>
                              {person.username
                                ? `@${person.username}`
                                : person.email || person.phoneNumber || person.kind}
                              {person.admin ? ' · Admin' : ''}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <Status value={person.status} />
                      </td>
                      <td className="numeric">{person.billCount}</td>
                      <td>{person.lastBillAt ? dateTime(person.lastBillAt) : 'No bills yet'}</td>
                      <td>
                        <button
                          className="admin-icon-button"
                          aria-label={`Investigate ${person.name}`}
                          onClick={() => onUser(person.id)}
                        >
                          <ArrowRight size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty
              title="No matching profiles"
              detail="Try another name or include all profiles and statuses."
            />
          )}
          <Pagination
            total={data.total}
            offset={offset}
            next={data.nextOffset}
            onPage={setOffset}
          />
        </>
      )}
    </section>
  );
}
type UserDetail = {
  user: Person;
  bills: BillRow[];
  billCount: number;
  timeline: {
    id: string;
    type: string;
    createdAt: string;
    billId?: string;
    error?: string;
    detail?: string;
  }[];
  eventCount: number;
  warnings: string[];
  asOf: string;
};
export function UserInspector({
  id,
  onClose,
  onBill,
  onNotifications,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onBill: (id: string) => void;
  onNotifications: (id: string, name: string) => void;
  onChanged: () => void;
}) {
  const [tab, setTab] = useState('activity'),
    [billOffset, setBillOffset] = useState(0),
    [eventOffset, setEventOffset] = useState(0);
  const [refresh, setRefresh] = useState(0),
    [action, setAction] = useState(''),
    [reason, setReason] = useState(''),
    [limit, setLimit] = useState(''),
    [saving, setSaving] = useState(false),
    [error, setError] = useState(''),
    [success, setSuccess] = useState('');
  const {
    data,
    loading,
    error: loadError,
  } = useReport<UserDetail>(`/users/${encodeURIComponent(id)}`, {}, refresh);
  const user = data?.user;
  const begin = (next: string) => {
    setAction(next);
    setReason('');
    setLimit(String(user?.receiptBillLimit ?? 10));
    setError('');
    setSuccess('');
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user || saving) return;
    setSaving(true);
    setError('');
    try {
      await api.post(`${endpoint}/users/${encodeURIComponent(id)}/controls`, {
        action,
        reason,
        expectedRevision: user.controlRevision,
        ...(action === 'quota'
          ? {
              limit: Number(limit),
            }
          : {}),
      });
      setSuccess(
        action === 'quota'
          ? 'Receipt limit updated.'
          : action === 'suspend'
            ? 'Account suspended.'
            : 'Account access restored.'
      );
      setAction('');
      setRefresh((n) => n + 1);
      onChanged();
    } catch (e) {
      setError(message(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
    >
      <DialogContent className="vs-design admin-ui admin-dialog">
        <DialogHeader>
          <DialogTitle>{user?.name || 'User investigation'}</DialogTitle>
          <DialogDescription>
            Account details, bill history, and recorded activity.
          </DialogDescription>
        </DialogHeader>
        <LoadState loading={loading} error={loadError} retry={() => setRefresh((n) => n + 1)} />
        {success && (
          <p role="status" className="admin-success">
            {success}
          </p>
        )}
        {user && data && (
          <div className="admin-stack">
            <div className="admin-section-heading">
              <div className="admin-person-cell">
                <span className="admin-avatar large">
                  {user.name
                    .split(' ')
                    .map((n) => n[0])
                    .slice(0, 2)
                    .join('')}
                </span>
                <div>
                  <strong>{user.username ? `@${user.username}` : user.kind}</strong>
                  <p>Joined {dateTime(user.createdAt)}</p>
                </div>
              </div>
              <Status value={user.status} />
            </div>
            <dl className="admin-profile-facts">
              <div>
                <dt>Email</dt>
                <dd>{user.email || 'Not added'}</dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>{user.phoneNumber || 'Not added'}</dd>
              </div>
              <div>
                <dt>Receiving methods</dt>
                <dd>{user.receivingProviders.join(', ') || 'None connected'}</dd>
              </div>
              <div>
                <dt>Receipt scans this month</dt>
                <dd>
                  {user.receiptBillsUsed} of {user.receiptBillLimit} · UTC
                </dd>
              </div>
            </dl>
            <details className="admin-identifiers">
              <summary>Account ID</summary>
              <code>{user.id}</code>
            </details>
            <div className="admin-section-heading">
              <div className="admin-segments" aria-label="User detail sections">
                {[
                  ['activity', 'Activity'],
                  ['bills', 'Bills'],
                  ['controls', 'Controls'],
                ].map(([id, label]) => (
                  <button key={id} aria-pressed={tab === id} onClick={() => setTab(id)}>
                    {label}
                  </button>
                ))}
              </div>
              <Button variant="outline" onClick={() => onNotifications(id, user.name)}>
                <Bell size={16} />
                Notification history
              </Button>
            </div>
            {tab === 'controls' && (
              <>
                <h3>Account controls</h3>
                {user.suspensionReason && (
                  <Notice>Suspension reason: {user.suspensionReason}</Notice>
                )}
                {user.kind === 'registered' && user.status !== 'deleted' ? (
                  <>
                    <div className="admin-actions">
                      <Button variant="outline" disabled={saving} onClick={() => begin('quota')}>
                        Change receipt limit
                      </Button>
                      {user.canRestore ? (
                        <Button
                          variant="outline"
                          disabled={saving}
                          onClick={() => begin('restore')}
                        >
                          Restore access
                        </Button>
                      ) : !user.admin && !['suspended', 'blocked'].includes(user.status) ? (
                        <Button
                          variant="outline"
                          className="admin-danger-text"
                          disabled={saving}
                          onClick={() => begin('suspend')}
                        >
                          Suspend account
                        </Button>
                      ) : null}
                      {user.admin && (
                        <span className="admin-muted">
                          <Shield size={15} /> Admin account
                        </span>
                      )}
                    </div>
                    {action && (
                      <form className="admin-control-form" onSubmit={submit}>
                        <h4>
                          {action === 'quota'
                            ? 'Change monthly receipt limit'
                            : action === 'suspend'
                              ? 'Suspend this account?'
                              : 'Restore account access?'}
                        </h4>
                        <p>
                          {action === 'quota'
                            ? 'The limit applies immediately. Used scans stay counted; usage resets next calendar month.'
                            : action === 'suspend'
                              ? 'Blocks authenticated access and disconnects active app sessions. Bills and payment records stay intact.'
                              : 'Restores the account’s status from before this dashboard suspended it.'}
                        </p>
                        {action === 'quota' && (
                          <label>
                            Monthly limit
                            <input
                              type="number"
                              min="0"
                              max="10000"
                              step="1"
                              required
                              value={limit}
                              onChange={(e) => setLimit(e.target.value)}
                            />
                          </label>
                        )}
                        <label>
                          Reason
                          <textarea
                            required
                            minLength={5}
                            maxLength={500}
                            placeholder="Explain the change for the activity record"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                          />
                        </label>
                        <p className="admin-muted">
                          Saved in the audit history as an admin dashboard action.
                        </p>
                        {error && (
                          <Notice danger>
                            <p>{error}</p>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setAction('');
                                setRefresh((n) => n + 1);
                              }}
                            >
                              Reload account
                            </Button>
                          </Notice>
                        )}
                        <div className="admin-actions">
                          <Button type="submit" disabled={saving || reason.trim().length < 5}>
                            {saving ? 'Saving…' : 'Confirm change'}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={saving}
                            onClick={() => setAction('')}
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    )}
                  </>
                ) : (
                  <p className="admin-muted">
                    Account controls are available for registered accounts.
                  </p>
                )}
              </>
            )}
            {data.warnings.map((w) => (
              <Notice key={w}>{w}</Notice>
            ))}
            {tab === 'bills' && (
              <section>
                <div className="admin-section-heading">
                  <div>
                    <h3>
                      Bills <span className="admin-count">{data.billCount}</span>
                    </h3>
                    <p>Created or participated in · latest 100 · excludes test bills</p>
                  </div>
                </div>
                {data.bills.length ? (
                  <>
                    <BillRows
                      bills={data.bills.slice(billOffset, billOffset + 10)}
                      onBill={onBill}
                    />
                    <Pagination
                      total={data.bills.length}
                      offset={billOffset}
                      next={billOffset + 10 < data.bills.length ? billOffset + 10 : null}
                      onPage={setBillOffset}
                      limit={10}
                    />
                  </>
                ) : (
                  <Empty
                    title="No bill activity"
                    detail="Created bills and participation will appear here."
                  />
                )}
              </section>
            )}
            {tab === 'activity' && (
              <section>
                <div className="admin-section-heading">
                  <div>
                    <h3>
                      Activity <span className="admin-count">{data.eventCount}</span>
                    </h3>
                    <p>Latest 100 recorded events · newest first</p>
                  </div>
                </div>
                {data.timeline.length ? (
                  <>
                    <ol className="admin-timeline">
                      {data.timeline.slice(eventOffset, eventOffset + 10).map((event, i) => (
                        <li key={`${event.id}-${i}`}>
                          <span className={`admin-timeline-dot ${event.error ? 'error' : ''}`} />
                          <div>
                            <strong>{humanize(event.type)}</strong>
                            {event.detail && <p>{event.detail}</p>}
                            {event.error && <p className="admin-danger-text">{event.error}</p>}
                            {event.billId && (
                              <button
                                className="admin-text-link secondary"
                                onClick={() => onBill(event.billId!)}
                              >
                                View bill <ArrowRight size={14} />
                              </button>
                            )}
                          </div>
                          <time>{dateTime(event.createdAt)}</time>
                        </li>
                      ))}
                    </ol>
                    <Pagination
                      total={data.timeline.length}
                      offset={eventOffset}
                      next={eventOffset + 10 < data.timeline.length ? eventOffset + 10 : null}
                      onPage={setEventOffset}
                      limit={10}
                    />
                  </>
                ) : (
                  <Empty
                    title="No recorded events"
                    detail="This account has no activity in the available event history."
                  />
                )}
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
